import { motion } from "framer-motion";
import { ArrowRight, TrendingUp, MapPin } from "lucide-react";

const LINES = ["Build a Thriving", "Legacy in North", "America's #1 Pet", "Services Franchise"];

export function Hero({ onNavigate, image }) {
  return (
    <section id="top" className="relative overflow-hidden pt-[72px]" data-testid="hero-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16 md:py-24 grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-xs uppercase tracking-[0.25em] font-semibold text-[#FF6B4A] mb-6"
          >
            Franchise Opportunity — 2026
          </motion.p>

          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.08] text-[#0D3B3E]">
            {LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden">
                <motion.span
                  className="block"
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.8, delay: 0.12 * i, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.55 }}
            className="mt-6 text-base sm:text-lg text-[#5C6B6C] max-w-xl leading-relaxed"
          >
            Own a premium dog daycare, boarding, and spa brand built on recurring membership revenue — backed by 300+
            locations and a $165B recession-resilient pet industry.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7 }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <a
              href="#lead-form"
              onClick={(e) => { e.preventDefault(); onNavigate("#lead-form"); }}
              data-testid="hero-cta-button"
              className="group inline-flex items-center gap-2 rounded-full bg-[#FF6B4A] hover:bg-[#E55636] text-white font-semibold px-7 py-3.5 transition-all hover:-translate-y-0.5 hover:shadow-xl"
            >
              Request Franchise Info
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </a>
            <a
              href="#financials"
              onClick={(e) => { e.preventDefault(); onNavigate("#financials"); }}
              data-testid="hero-secondary-cta"
              className="inline-flex items-center gap-2 rounded-full border-2 border-[#0D3B3E] text-[#0D3B3E] font-semibold px-7 py-3.5 hover:bg-[#0D3B3E] hover:text-white transition-colors"
            >
              See the Economics
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-10 flex flex-wrap gap-x-10 gap-y-4"
          >
            {[
              ["300+", "Locations Nationwide"],
              ["$165B", "Pet Industry Market"],
              ["90", "Net Promoter Score"],
            ].map(([v, l]) => (
              <div key={l}>
                <div className="font-display text-2xl font-extrabold text-[#0D3B3E]">{v}</div>
                <div className="text-xs uppercase tracking-widest text-[#5C6B6C] mt-1">{l}</div>
              </div>
            ))}
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="rounded-t-[999px] rounded-b-[2rem] overflow-hidden border-8 border-white shadow-2xl aspect-[4/5]">
              <img src={image} alt="Happy golden retriever at Dogtopia daycare" className="w-full h-full object-cover" />
            </div>
            <div className="animate-float absolute -left-4 sm:-left-8 top-16 bg-white rounded-2xl shadow-xl px-5 py-4 border border-[#E4DFD3]">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-[#FF6B4A]" />
                <span className="font-display font-extrabold text-[#0D3B3E]" data-testid="hero-stat-avg-sales">$927K</span>
              </div>
              <div className="text-[11px] uppercase tracking-widest text-[#5C6B6C] mt-0.5">Avg Gross Sales*</div>
            </div>
            <div className="animate-float-delayed absolute -right-3 sm:-right-6 bottom-16 bg-[#0D3B3E] rounded-2xl shadow-xl px-5 py-4">
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-[#F7C948]" />
                <span className="font-display font-extrabold text-white">300+</span>
              </div>
              <div className="text-[11px] uppercase tracking-widest text-white/70 mt-0.5">Open Locations</div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
